/**
 * MCP Service - Frontend client for the MCP Server
 *
 * This service provides methods to interact with the SimCheck MCP Server
 * for AI agent functionality.
 *
 * Authentication is automatic using the user's Firebase session.
 * No manual API key configuration needed for internal use.
 */

import { auth } from '../config/firebase';
import {
  ChatMessage,
  ChatResponse,
  Conversation,
  Approval,
  ApprovalResponse,
  MCPTool,
  MCPResource,
  MCPToolResult,
} from '../types/chat';
import { MCP_SERVER_URL } from '@/config/env';


/**
 * Get the current user's Firebase ID token for authentication
 */
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;

  if (!user) {
    throw new Error('Not authenticated. Please log in.');
  }

  // Get a fresh ID token
  const token = await user.getIdToken();
  return token;
}

/**
 * Get the current organization ID (for view-as-org or regular users)
 */
function getCurrentOrganizationId(): string | null {
  // Check for view-as-org state (used by system admins)
  // Key matches AuthContext: localStorage.setItem('viewAsOrgId', ...)
  const viewAsOrgId = localStorage.getItem('viewAsOrgId');
  if (viewAsOrgId) {
    return viewAsOrgId;
  }

  // Also check activeOrganizationId for regular users
  const activeOrgId = localStorage.getItem('activeOrganizationId');
  if (activeOrgId) {
    return activeOrgId;
  }

  // For users without localStorage state, the organization will be fetched from the user document on the server
  return null;
}

/**
 * Make an authenticated request to the MCP server
 * Uses Firebase ID token automatically - no manual API key needed
 */
async function mcpRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();
  const orgId = getCurrentOrganizationId();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  // Pass organization ID for system admins using view-as-org
  if (orgId) {
    headers['X-Organization-Id'] = orgId;
  }

  const response = await fetch(`${MCP_SERVER_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || error.message || `Request failed: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// Chat API
// ============================================================================

/**
 * Send a chat message and get a response
 */
export async function sendChatMessage(
  message: string,
  conversationId?: string
): Promise<ChatResponse> {
  return mcpRequest<ChatResponse>('/api/chat', {
    method: 'POST',
    body: JSON.stringify({ message, conversationId }),
  });
}

/**
 * Get or create an active conversation
 */
export async function getOrCreateConversation(): Promise<Conversation> {
  const response = await mcpRequest<{ conversation: Conversation }>('/api/conversations', {
    method: 'POST',
  });
  return response.conversation;
}

/**
 * List user's conversations
 */
export async function listConversations(
  options: { limit?: number; status?: 'active' | 'closed' } = {}
): Promise<Conversation[]> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', options.limit.toString());
  if (options.status) params.set('status', options.status);

  const response = await mcpRequest<{ conversations: Conversation[] }>(
    `/api/conversations?${params.toString()}`
  );
  return response.conversations;
}

/**
 * Get messages from a conversation
 */
export async function getConversationMessages(
  conversationId: string,
  limit: number = 50
): Promise<ChatMessage[]> {
  const response = await mcpRequest<{ messages: ChatMessage[] }>(
    `/api/conversations/${conversationId}/messages?limit=${limit}`
  );
  return response.messages;
}

/**
 * Close a conversation
 */
export async function closeConversation(conversationId: string): Promise<void> {
  await mcpRequest(`/api/conversations/${conversationId}/close`, {
    method: 'POST',
  });
}

// ============================================================================
// Approvals API (HITL)
// ============================================================================

/**
 * Get pending approvals
 */
export async function getPendingApprovals(): Promise<Approval[]> {
  const response = await mcpRequest<{ approvals: Approval[] }>('/api/approvals');
  return response.approvals;
}

/**
 * Approve a pending action
 */
export async function approveAction(approvalId: string): Promise<ApprovalResponse> {
  return mcpRequest<ApprovalResponse>(`/api/approvals/${approvalId}/approve`, {
    method: 'POST',
  });
}

/**
 * Reject a pending action
 */
export async function rejectAction(
  approvalId: string,
  reason?: string
): Promise<ApprovalResponse> {
  return mcpRequest<ApprovalResponse>(`/api/approvals/${approvalId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ============================================================================
// MCP Protocol API
// ============================================================================

/**
 * List available MCP tools
 */
export async function listTools(): Promise<MCPTool[]> {
  const response = await mcpRequest<{ tools: MCPTool[] }>('/message', {
    method: 'POST',
    body: JSON.stringify({ type: 'list_tools' }),
  });
  return response.tools;
}

/**
 * Call an MCP tool directly
 */
export async function callTool(
  name: string,
  args: Record<string, unknown> = {}
): Promise<MCPToolResult> {
  return mcpRequest<MCPToolResult>('/message', {
    method: 'POST',
    body: JSON.stringify({
      type: 'call_tool',
      params: { name, arguments: args },
    }),
  });
}

/**
 * List available MCP resources
 */
export async function listResources(): Promise<MCPResource[]> {
  const response = await mcpRequest<{ resources: MCPResource[] }>('/message', {
    method: 'POST',
    body: JSON.stringify({ type: 'list_resources' }),
  });
  return response.resources;
}

/**
 * Read an MCP resource
 */
export async function readResource(uri: string): Promise<string> {
  const response = await mcpRequest<{ content: string }>('/message', {
    method: 'POST',
    body: JSON.stringify({
      type: 'read_resource',
      params: { uri },
    }),
  });
  return response.content;
}

// ============================================================================
// Health Check
// ============================================================================

/**
 * Check if the MCP server is available
 */
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${MCP_SERVER_URL}/health`);
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Check if the user is authenticated and can access the MCP server
 */
export async function checkAuth(): Promise<boolean> {
  try {
    await mcpRequest('/api/conversations');
    return true;
  } catch {
    return false;
  }
}
